/**
 * Platform Admin · Audit Log viewer (Slice 3)
 *
 * Shows recent admin actions: school.deactivate / activate / delete / user.delete.
 * Read-only. /platform-admin/audit-log.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, History, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ACTION_LABELS = {
  "school.deactivate": { label: "School Deactivated", className: "bg-amber-100 text-amber-800" },
  "school.activate":   { label: "School Reactivated", className: "bg-emerald-100 text-emerald-800" },
  "school.delete":     { label: "School Deleted",     className: "bg-red-100 text-red-800" },
  "user.delete":       { label: "User Deleted",       className: "bg-rose-100 text-rose-800" },
};

export default function PlatformAdminAuditLog() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/audit-log", {
        params: { action: filter || undefined, limit: 200 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate("/platform-admin")}
            className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm"
            data-testid="audit-log-back"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div className="border-l border-slate-200 pl-3 ml-2 flex items-center gap-2">
            <History size={18} className="text-slate-500" />
            <h1 className="font-display font-bold text-lg">Audit Log</h1>
            <span className="text-xs text-slate-500 font-mono">· {total} actions</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2 mb-4" data-testid="audit-log-filters">
          {["", "school.deactivate", "school.activate", "school.delete", "user.delete"].map((a) => (
            <Button
              key={a || "all"}
              variant={filter === a ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(a)}
              data-testid={`audit-filter-${a || "all"}`}
            >
              {a ? ACTION_LABELS[a].label : "All"}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="bg-white border rounded-xl py-12 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border rounded-xl py-12 text-center text-slate-400" data-testid="audit-log-empty">
            No audit entries match this filter.
          </div>
        ) : (
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">When</th>
                  <th className="text-left px-4 py-3 font-medium">Action</th>
                  <th className="text-left px-4 py-3 font-medium">Target</th>
                  <th className="text-left px-4 py-3 font-medium">Snapshot</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => {
                  const meta = ACTION_LABELS[r.action] || { label: r.action, className: "bg-slate-100 text-slate-800" };
                  const when = r.created_at ? new Date(r.created_at).toLocaleString() : "—";
                  return (
                    <tr key={r.action_id} data-testid={`audit-row-${r.action_id}`}>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{when}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${meta.className}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{r.target_type}: {r.target_id}</td>
                      <td className="px-4 py-3">
                        {r.snapshot?.name && (
                          <div className="font-medium text-slate-900">{r.snapshot.name}</div>
                        )}
                        {r.snapshot?.email && (
                          <div className="text-xs text-slate-500 font-mono">{r.snapshot.email}</div>
                        )}
                        {r.snapshot?.role && (
                          <div className="text-xs text-slate-500">role: {r.snapshot.role}</div>
                        )}
                        {r.snapshot?.counts && (
                          <details className="mt-1">
                            <summary className="text-xs text-slate-500 cursor-pointer">cascade counts</summary>
                            <pre className="text-[10px] mt-1 bg-slate-50 p-2 rounded overflow-auto">
                              {JSON.stringify(r.snapshot.counts, null, 2)}
                            </pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
